// The one schedule form (docs/tasks/schedules-unified.md §2, §9): Who ·
// When · What · Report, read from a row and turned back into the body the
// server takes (routes/schedules_unified.py, services/schedules/model.py).
// Pure; scheduleFormModel.selftest.ts runs it.
//
//   Who     me (the owner agent itself) · an agent or a subagent · the team ·
//           a saved workflow · a reminder line (message), plus the OWNER: the
//           agent whose chat the answer (or the line) comes from
//   When    a timetable (cron in a zone) · every N minutes/hours/days · once ·
//           after another workflow's run ends (a workflow only: ./chainModel.ts)
//   What    the brief (the worker sees only this), or the reminder line
//           (sent word for word), or a workflow's parameters
//   Report  in chat, word for word (default) · by email · quiet; a team or a
//           workflow reports through its own end-of-run settings
//
// What the server would refuse is said here first (problems), so the form
// can point at the field; the server re-checks everything at save.

import type { ScheduleBody, SchedulePatchBody, ScheduleReport, ScheduleWhat, ScheduleWhen, UnifiedSchedule } from "@/api/schedulesUnified";
import { MAX_BRIEF, MAX_TITLE, MIN_MINUTES, TASK_DEFAULT_MINUTES, TASK_MAX_MINUTES, TEAM_MAX_MINUTES, TEAM_MIN_MINUTES } from "@/features/tasks/taskModel";
import { DEFAULT_TZ } from "@/features/workflows/editor/scheduleModel";
import { nextFires, parseCron } from "@/lib/cron";

import type { AfterOn } from "./chainModel";

export type WhoChoice = "me" | "agent" | "team" | "workflow" | "message";
export type WhenChoice = "cron" | "every" | "at" | "after";
export type EveryUnit = "minutes" | "hours" | "days";
export type ReportChoice = "chat" | "email" | "quiet";
export type ChannelChoice = "auto" | "telegram" | "whatsapp";

export const MAX_NAME = 80;
export const MAX_LINE = 1000;
export const FLOOR_MINUTES = 15;
export const HIGH_FREQUENCY_FLOOR = 1;
export const EVERY_MAX_MINUTES = 10_080;
export const AT_MAX_DAYS = 400;
export const MAX_EMAILS = 10;
export const DEFAULT_CRON = "0 8 * * 1-5";

const UNIT_MINUTES: Readonly<Record<EveryUnit, number>> = { minutes: 1, hours: 60, days: 1440 };

export interface ScheduleForm {
  name: string;
  /** The agent whose chat it reports in ("" = none: a team, a workflow, a quiet row made without one). */
  owner: string;
  who: WhoChoice;
  /** An agent or subagent template (who = agent), a workflow slug (who = workflow). */
  ref: string;
  when: WhenChoice;
  cron: string;
  tz: string;
  every: number;
  everyUnit: EveryUnit;
  /** The interval the row was saved with and its phase: kept when the interval is unchanged. */
  savedEvery: { minutes: number; anchor: string } | null;
  /** "YYYY-MM-DDTHH:MM", the browser's local time. */
  at: string;
  /** The instant the row was saved with: sent back unchanged when `at` still shows it. */
  savedAt: string | null;
  highFrequency: boolean;
  /** The workflow whose run sets this one off (when = after). */
  afterWorkflow: string;
  afterOn: AfterOn;
  brief: string;
  title: string;
  question: boolean;
  /** Minutes per run (NaN = not given). */
  minutes: number;
  needsBrowser: boolean;
  params: Record<string, unknown>;
  report: ReportChoice;
  channel: ChannelChoice;
  /** The operator's id on `channel` ("" = the first one Lattice knows). */
  to: string;
  emailTo: string[];
  enabled: boolean;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** A Date as the `datetime-local` value of the browser's own clock. */
export function localInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Tomorrow at 09:00, the browser's time: where a one-off starts. */
export function defaultAt(now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 9, 0);
  return localInput(d);
}

export interface FormSeed {
  owner?: string;
  who?: WhoChoice;
  ref?: string;
  when?: WhenChoice;
}

/** A new schedule: the owner (and who) the page opened it for, weekdays at 08:00, reported in chat. */
export function emptyForm(seed: FormSeed = {}, now: Date = new Date()): ScheduleForm {
  return {
    name: "", owner: seed.owner ?? "", who: seed.who ?? "me", ref: seed.ref ?? "", when: seed.when ?? "cron", cron: DEFAULT_CRON,
    tz: DEFAULT_TZ, every: 2, everyUnit: "hours", savedEvery: null, at: defaultAt(now), savedAt: null,
    highFrequency: false, afterWorkflow: "", afterOn: "completed", brief: "", title: "", question: false, minutes: TASK_DEFAULT_MINUTES, needsBrowser: false,
    params: {}, report: "chat", channel: "auto", to: "", emailTo: [], enabled: true,
  };
}

function everyParts(minutes: number): { every: number; everyUnit: EveryUnit } {
  if (minutes % 1440 === 0) return { every: minutes / 1440, everyUnit: "days" };
  if (minutes % 60 === 0) return { every: minutes / 60, everyUnit: "hours" };
  return { every: minutes, everyUnit: "minutes" };
}

/** A saved row read into the form. */
export function formFromSchedule(s: UnifiedSchedule, now: Date = new Date()): ScheduleForm {
  const base = emptyForm({ owner: s.owner }, now);
  const kind = s.who.kind;
  const who: WhoChoice = kind === "subagent" ? "agent" : kind;
  const w = s.when;
  const when: WhenChoice = w.after?.workflow ? "after" : w.cron ? "cron" : w.every_minutes ? "every" : "at";
  const at = w.at ? new Date(w.at) : null;
  const rep = s.report ?? null;
  return {
    ...base,
    name: s.name,
    who,
    ref: s.who.ref,
    when,
    cron: w.cron ?? base.cron,
    tz: w.tz || DEFAULT_TZ,
    ...(w.every_minutes ? everyParts(w.every_minutes) : {}),
    savedEvery: w.every_minutes ? { minutes: w.every_minutes, anchor: w.anchor_at } : null,
    at: at && !Number.isNaN(at.getTime()) ? localInput(at) : base.at,
    savedAt: w.at ?? null,
    highFrequency: w.high_frequency,
    afterWorkflow: w.after?.workflow ?? "",
    afterOn: w.after?.on === "completed_or_failures" ? "completed_or_failures" : "completed",
    brief: s.what.brief,
    title: s.what.title,
    question: s.what.kind === "question",
    // a Team row saved without minutes re-saves with the default, never NaN
    minutes: s.what.minutes ?? TASK_DEFAULT_MINUTES,
    needsBrowser: s.what.needs_browser,
    params: { ...(s.what.params ?? {}) },
    report: rep ? rep.mode : "quiet",
    channel: rep ? rep.channel : "auto",
    to: rep?.to ?? "",
    emailTo: [...(rep?.email_to ?? [])],
    enabled: s.enabled,
  };
}

// ─── what the form says ───

/** A team or a workflow reports through its own end-of-run settings. */
export function reportsBack(who: WhoChoice): boolean {
  return who === "me" || who === "agent" || who === "message";
}

/** Whose chat it goes through is needed: `me`, a reminder, or an answer told in chat / by email. */
export function needsOwner(f: Pick<ScheduleForm, "who" | "report">): boolean {
  return f.who === "me" || f.who === "message" || (f.who === "agent" && f.report !== "quiet");
}

/** The fewest minutes between two fires (1 for a reminder the operator asked to have that often). */
export function floorMinutes(f: Pick<ScheduleForm, "who" | "highFrequency">): number {
  return f.who === "message" && f.highFrequency ? HIGH_FREQUENCY_FLOOR : FLOOR_MINUTES;
}

export function everyMinutes(f: Pick<ScheduleForm, "every" | "everyUnit">): number {
  return Math.round(f.every * UNIT_MINUTES[f.everyUnit]);
}

/** A cron the server refuses: unreadable, or fires under `floor` minutes apart. */
export function cronGapProblem(cron: string, floor: number): string | null {
  if (!parseCron(cron)) return "Five fields: minute hour day month weekday (e.g. 30 6 * * 1-5).";
  const fires = nextFires(cron, new Date(2026, 0, 5), 60);
  for (let i = 1; i < fires.length; i += 1) {
    if (fires[i].getTime() - fires[i - 1].getTime() < floor * 60_000) return `At least ${floor} minutes apart.`;
  }
  return null;
}

export type FormField = "name" | "owner" | "ref" | "when" | "brief" | "title" | "minutes" | "email";

/** What stops the form from being saved, by field ({} = it can go). */
export function formProblems(f: ScheduleForm, now: Date = new Date()): Partial<Record<FormField, string>> {
  const out: Partial<Record<FormField, string>> = {};
  const name = f.name.trim();
  if (!name) out.name = "Give it a name.";
  else if (name.length > MAX_NAME) out.name = `At most ${MAX_NAME} characters.`;
  if (needsOwner(f) && !f.owner) out.owner = f.who === "message" ? "Say whose chat the reminder comes from." : "Say whose chat it reports in.";
  if ((f.who === "agent" || f.who === "workflow") && !f.ref) out.ref = f.who === "workflow" ? "Choose the workflow." : "Choose who does it.";
  const floor = floorMinutes(f);
  if (f.when === "after") {
    if (f.who !== "workflow") out.when = "Only a workflow can run after another one: choose a workflow in Who, or another When.";
    else if (!f.afterWorkflow) out.when = "Choose the workflow it runs after.";
    else if (f.afterWorkflow === f.ref) out.when = "A workflow cannot run after itself.";
  } else if (f.who === "workflow" && f.when !== "cron") out.when = "A workflow runs on a timetable or after another workflow: choose Repeats or After a workflow.";
  else if (f.when === "cron") {
    const p = cronGapProblem(f.cron, floor);
    if (p) out.when = p;
  } else if (f.when === "every") {
    const m = everyMinutes(f);
    if (!Number.isFinite(f.every) || f.every <= 0 || !Number.isInteger(m)) out.when = "A whole number of minutes, hours or days.";
    else if (m < floor) out.when = `At least every ${floor} minutes.`;
    else if (m > EVERY_MAX_MINUTES) out.when = "At most once a week.";
  } else {
    const t = new Date(f.at).getTime();
    if (!f.at || Number.isNaN(t)) out.when = "Choose a day and a time.";
    else if (t < now.getTime() + 60_000 && !atUnchanged(f)) out.when = "That time has passed.";
    else if (t > now.getTime() + AT_MAX_DAYS * 86_400_000) out.when = `At most ${AT_MAX_DAYS} days ahead.`;
  }
  const text = f.brief.trim();
  if (f.who === "message") {
    if (!text) out.brief = "Write the line to send.";
    else if (text.replace(/\s+/g, " ").length > MAX_LINE) out.brief = `At most ${MAX_LINE.toLocaleString("en-GB")} characters.`;
  } else if (f.who !== "workflow") {
    if (!text) out.brief = "Say what to do.";
    else if (text.length > MAX_BRIEF) out.brief = `At most ${MAX_BRIEF.toLocaleString("en-GB")} characters.`;
  }
  if (f.title.trim().length > MAX_TITLE) out.title = `At most ${MAX_TITLE} characters.`;
  const limit = minutesLimit(f);
  if (limit && (!Number.isInteger(f.minutes) || f.minutes < limit.min || f.minutes > limit.max)) out.minutes = `${limit.min} to ${limit.max} minutes.`;
  if (reportsBack(f.who) && f.who !== "message" && f.report === "email") {
    if (f.emailTo.length === 0) out.email = "Add at least one address.";
    else if (f.emailTo.length > MAX_EMAILS) out.email = `At most ${MAX_EMAILS} addresses.`;
  }
  return out;
}

/** The time limit a run takes, when the form asks for one (null: none asked: a reminder, a question, a workflow). */
export function minutesLimit(f: Pick<ScheduleForm, "who" | "question">): { min: number; max: number } | null {
  if (f.who === "team") return { min: TEAM_MIN_MINUTES, max: TEAM_MAX_MINUTES };
  if ((f.who === "me" || f.who === "agent") && !f.question) return { min: MIN_MINUTES, max: TASK_MAX_MINUTES };
  return null;
}

// ─── the body ───

/** The one-off still shows the instant it was saved with. */
function atUnchanged(f: Pick<ScheduleForm, "at" | "savedAt">): boolean {
  const saved = f.savedAt ? new Date(f.savedAt) : null;
  return !!saved && !Number.isNaN(saved.getTime()) && localInput(saved) === f.at;
}

export function whenBody(f: ScheduleForm): ScheduleWhen {
  const common = { tz: f.tz || DEFAULT_TZ, anchor_at: "", high_frequency: f.who === "message" && f.highFrequency };
  if (f.when === "after") return { ...common, high_frequency: false, cron: null, every_minutes: null, at: null, after: { workflow: f.afterWorkflow, on: f.afterOn } };
  if (f.when === "cron") return { ...common, cron: f.cron.trim().replace(/\s+/g, " "), every_minutes: null, at: null };
  if (f.when === "every") {
    const minutes = everyMinutes(f);
    const anchor = f.savedEvery && f.savedEvery.minutes === minutes ? f.savedEvery.anchor : "";
    return { ...common, anchor_at: anchor, cron: null, every_minutes: minutes, at: null };
  }
  return { ...common, cron: null, every_minutes: null, at: atUnchanged(f) && f.savedAt ? f.savedAt : new Date(f.at).toISOString() };
}

export function whatBody(f: ScheduleForm): ScheduleWhat {
  const none = { title: "", kind: "task" as const, minutes: null, params: {}, needs_browser: false };
  if (f.who === "message") return { ...none, brief: f.brief.trim().replace(/\s+/g, " ") };
  if (f.who === "workflow") return { ...none, brief: "", params: { ...f.params } };
  if (f.who === "team") return { ...none, brief: f.brief.trim(), title: f.title.trim(), minutes: Number.isFinite(f.minutes) ? f.minutes : null };
  return {
    brief: f.brief.trim(), title: f.title.trim(), kind: f.question ? "question" : "task",
    minutes: f.question || !Number.isFinite(f.minutes) ? null : f.minutes, params: {}, needs_browser: f.needsBrowser,
  };
}

export function reportBody(f: ScheduleForm): ScheduleReport | null {
  if (!reportsBack(f.who)) return null;
  const none = { thread: "", email_to: [] as string[] };
  const mode: ReportChoice = f.who === "message" ? "chat" : f.report;
  if (mode === "chat") return { ...none, mode, channel: f.channel, to: f.channel === "auto" ? "" : f.to };
  if (mode === "email") return { ...none, mode, channel: "auto", to: "", email_to: [...f.emailTo] };
  return { ...none, mode, channel: "auto", to: "" };
}

/** The target; an agent template that is a subagent says so (the server would say it too). */
export function whoBody(f: ScheduleForm, isSubagent: (template: string) => boolean): { kind: UnifiedSchedule["who"]["kind"]; ref: string } {
  if (f.who === "agent") return { kind: isSubagent(f.ref) ? "subagent" : "agent", ref: f.ref };
  if (f.who === "workflow") return { kind: "workflow", ref: f.ref };
  return { kind: f.who, ref: "" };
}

function ownerOf(f: ScheduleForm): string {
  return f.who === "team" || f.who === "workflow" ? "" : f.owner;
}

/** The create body. `force` + a reason make it although another row has the same slot and recipient. */
export function scheduleBody(f: ScheduleForm, isSubagent: (t: string) => boolean, force?: { reason: string }): ScheduleBody {
  return {
    name: f.name.trim(), enabled: f.enabled, when: whenBody(f), who: whoBody(f, isSubagent), what: whatBody(f),
    report: reportBody(f), owner: ownerOf(f), force: !!force, distinct_reason: force?.reason.trim() ?? "",
  };
}

/** An edit sends every field (a team / workflow row clears its report). */
export function schedulePatch(f: ScheduleForm, isSubagent: (t: string) => boolean, force?: { reason: string }): SchedulePatchBody {
  const report = reportBody(f);
  return {
    name: f.name.trim(), when: whenBody(f), who: whoBody(f, isSubagent), what: { ...whatBody(f) },
    report: report ? { ...report } : null, clear_report: report === null, owner: ownerOf(f), force: !!force,
    distinct_reason: force?.reason.trim() ?? "",
  };
}

/** "remind-call-emma" from what it says: a starting name the operator can change. */
export function suggestName(f: Pick<ScheduleForm, "who" | "brief" | "ref"> & Partial<Pick<ScheduleForm, "when" | "afterWorkflow">>): string {
  const chained = f.who === "workflow" && f.when === "after" && !!f.afterWorkflow && !!f.ref;
  const source = chained ? `${f.ref} after ${f.afterWorkflow}` : f.who === "workflow" ? f.ref : f.brief;
  const words = source.toLowerCase().match(/[a-z0-9]+/g) ?? [];
  const slug = words.slice(0, chained ? 12 : 6).join("-").slice(0, chained ? 64 : 48).replace(/-+$/, "");
  if (!slug) return "";
  return f.who === "message" ? `remind-${slug}` : slug;
}
